// Strona listy wszystkich artykułów z bazy wiedzy

import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import classnames from 'classnames';
import qs from 'query-string';

import { reduxActions, useDispatch } from 'store';
import { useGetKnowledgeBaseArticles } from 'api';
import { IKnowledgeBaseArticle } from 'api/endpoints/useGetKnowledgeBaseArticles';
import { Container, Breadcrumbs, Loader, Pagination } from 'components/controls';
import { useAppNavigate } from 'hooks';
import { useLocation } from 'react-router-dom';
import WiseSelect from 'components/wise/WiseSelect';

import styles from 'theme/pages/KnowledgeBaseAll/KnowledgeBaseAll.module.scss';

interface ISortOption {
  value: string;
  label: string;
  sort_by: 'created_at' | 'updated_at';
  sort_order: 'asc' | 'desc';
}

const KnowledgeBaseAll = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useAppNavigate();
  const location = useLocation();

  // Opcje sortowania
  const sortOptions: ISortOption[] = [
    {
      value: 'created_at_desc',
      label: t('Najnowsze'),
      sort_by: 'created_at',
      sort_order: 'desc'
    },
    {
      value: 'created_at_asc',
      label: t('Najstarsze'),
      sort_by: 'created_at',
      sort_order: 'asc'
    },
    {
      value: 'updated_at_desc',
      label: t('Ostatnio zaktualizowane'),
      sort_by: 'updated_at',
      sort_order: 'desc'
    }
  ];

  // Parametry zapytania do API - czytane z URL
  const urlParams = qs.parse(location.search, { parseNumbers: true });
  const currentPage = (urlParams.page as number) || 1;
  const limit = 8;
  const currentSortValue = (urlParams.sort as string) || 'created_at_desc';
  const currentSort = sortOptions.find((opt) => opt.value === currentSortValue) || sortOptions[0];

  // Pobranie listy artykułów
  const { data: articlesData, isLoading: isLoadingArticles } = useGetKnowledgeBaseArticles(
    {
      page: currentPage,
      limit: limit,
      sort_by: currentSort.sort_by,
      sort_order: currentSort.sort_order
    },
    {
      keepPreviousData: true
    }
  );

  const articles = articlesData?.items || [];

  // Funkcja pomocnicza do wyciągania danych z article_fields
  const getArticleData = (article: IKnowledgeBaseArticle) => {
    const POPULAR_VALUE = '1';

    // Znajdź pole z obrazem (section_field_symbol === "image")
    const imageField = article.article_fields?.find(
      (field) => field.section_field_symbol === 'image' && field.file
    );

    // Znajdź pole z tytułem (section_field_symbol === "title")
    const titleField = article.article_fields?.find(
      (field) => field.section_field_symbol === 'title'
    );

    // Znajdź pole z podtytułem (section_field_symbol === "Podtytuł")
    const subtitleField = article.article_fields?.find(
      (field) => field.section_field_symbol === 'Podtytuł'
    );

    // Sprawdź czy artykuł jest popularny (section_field_symbol === "popular" lub field_name === "popular")
    const popularField = article.article_fields?.find(
      (field) =>
        (field.section_field_symbol === 'popular' || field.field_name === 'popular') &&
        typeof field.value === 'string' &&
        field.value === POPULAR_VALUE
    );

    let image = '';
    const titleValue = typeof titleField?.value === 'string' ? titleField.value : '';
    const subtitleValue = typeof subtitleField?.value === 'string' ? subtitleField.value : '';
    const name = article.title || titleValue;
    const description = article.subtitle || subtitleValue;
    const isPopular = !!popularField;

    // Wyciągnij URL obrazka z translations lub iterator
    if (imageField?.file) {
      const translations = imageField.file.translations || imageField.file.iterator || [];
      const currentLangTranslation =
        translations.find((t) => t.language === i18n.language) || translations[0];

      if (currentLangTranslation?.url) {
        image = currentLangTranslation.url;
      }
    }

    return { image, name, description, isPopular };
  };

  // Funkcja zmiany strony
  const handlePageChange = (page: number) => {
    const queryParams: Record<string, any> = { page };
    if (currentSortValue !== 'created_at_desc') {
      queryParams.sort = currentSortValue;
    }
    const navigationUrl = `/knowledge-base/all?${qs.stringify(queryParams, {
      skipEmptyString: true
    })}`;
    navigate(navigationUrl, { replace: true });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Funkcja zmiany sortowania
  const handleSortChange = (option: ISortOption | null) => {
    if (option) {
      const queryParams: Record<string, any> = { page: 1 };
      if (option.value !== 'created_at_desc') {
        queryParams.sort = option.value;
      }
      const navigationUrl = `/knowledge-base/all?${qs.stringify(queryParams, {
        skipEmptyString: true
      })}`;
      navigate(navigationUrl, { replace: true });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Ustawienie breadcrumbs'ów
  useEffect(() => {
    dispatch(
      reduxActions.setBreadcrumbs([
        {
          name: t('Baza wiedzy'),
          path: '/knowledge-base'
        },
        {
          name: t('Wszystkie artykuły'),
          path: undefined
        }
      ])
    );
  }, []);

  return (
    <div className={classnames(styles.componentWrapper, 'StylePath-Pages-KnowledgeBaseAll')}>
      <div className={styles.breadcrumbsWrapper}>
        <Breadcrumbs />
      </div>
      <div className={styles.backgroundWrapper}>
        <Container>
          <div className={styles.content}>
            <div className={styles.header}>
              <h1 className={styles.title}>
                {t('Wszystkie artykuły')}
                {articlesData?.total_count && (
                  <span className={styles.count}> ({articlesData.total_count})</span>
                )}
              </h1>
            </div>
          </div>
        </Container>
      </div>

      <div className={styles.articlesSection}>
        <Container>
          <div className={styles.sortingWrapper}>
            <div className={styles.sortingSelectWrapper}>
              <WiseSelect<ISortOption>
                onChange={handleSortChange}
                value={currentSortValue}
                options={sortOptions.map((opt) => ({
                  value: opt.value,
                  label: opt.label,
                  item: opt
                }))}
                placeholder={t('Sortowanie')}
              />
            </div>
          </div>
          {isLoadingArticles ? (
            <Loader />
          ) : articles.length > 0 ? (
            <>
              <div className={styles.articlesGridWrapper} itemScope itemType="http://schema.org/ItemList">
                {articles.map((article: IKnowledgeBaseArticle) => {
                  const { image, name, description, isPopular } = getArticleData(article);
                  return (
                    <div
                      key={article.id}
                      className={styles.articleWrapper}
                      itemProp="itemListElement"
                      itemScope
                      itemType="http://schema.org/ListItem">
                      <div className={styles.articleCard}>
                        {image && (
                          <div className={styles.articleImageWrapper}>
                            <img src={image} alt={name} className={styles.articleImage} />
                            {isPopular && (
                              <div className={styles.popularFlag}>{t('Popularne')}</div>
                            )}
                          </div>
                        )}
                        <div className={styles.articleContent}>
                          <h3 className={styles.articleTitle}>{name}</h3>
                          {description && (
                            <p className={styles.articleDescription} dangerouslySetInnerHTML={{ __html: description }} />
                          )}
                          <Link
                            to={`/knowledge-base/${article.id}`}
                            className={styles.readMoreButton}>
                            {t('Czytaj więcej')}
                          </Link>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              <Pagination
                pagesCount={articlesData?.total_pages || 1}
                page={currentPage}
                onChange={handlePageChange}
              />
            </>
          ) : (
            <div>{t('Brak artykułów')}</div>
          )}
        </Container>
      </div>
    </div>
  );
};

export default KnowledgeBaseAll;

